import { Fragment, ReactNode } from 'react';

import { useIsSecuritySunsetEnabled } from 'features/organization/security-insights/hooks/useIsSecuritySunsetEnabled';

import {
  AgentAutoscalerSetup,
  AgentCostSetup,
  AgentGpuMetricsSetup,
  AgentNetworkCostSetup,
  AgentOmniSetup,
  AgentSecuritySetup,
  AgentWorkloadAutoscalerSetup,
  AgentNvidiaDevicePluginSetup,
} from 'hooks/dialogs/cluster/components/features';
import {
  EXTRA_FEATURES,
  FEATURE_TO_TITLE,
  MAIN_FEATURES,
} from 'hooks/dialogs/cluster/constants';
import { SelectAutoscalerType } from './SelectAutoscalerType';

import {
  FeatureSelection,
  FeatureName,
  ProductFeatures,
  AutoscalerType,
} from '../../types';
import {
  Typography,
  Box,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';

type SetupComponentItem = {
  feature: FeatureName;
  component: ReactNode;
  isAccessible?: boolean;
};

type Props = {
  featureSelection: FeatureSelection;
  updateFeature: (product: FeatureName, checked: boolean) => void;
  alreadyInstalledFeatures?: Partial<ProductFeatures>;
  isEKSProvider: boolean;
} & (
  | {
      shouldShowAutoscalerSelection: true;
      autoscalerType: AutoscalerType;
      onSelectAutoscalerType: (autoscalerType: AutoscalerType) => void;
    }
  | {
      shouldShowAutoscalerSelection: false;
    }
);

export function SimpleFeaturesSection({
  featureSelection,
  updateFeature,
  alreadyInstalledFeatures,
  isEKSProvider,
  ...autoscalerSelectionProps
}: Props) {
  const isSecuritySunsetEnabled = useIsSecuritySunsetEnabled();
  const shouldShowFullFeatureSelection =
    !autoscalerSelectionProps.shouldShowAutoscalerSelection ||
    autoscalerSelectionProps.autoscalerType !== 'karpenter';

  const featureComponentMap: Record<
    FeatureName,
    { component: ReactNode; isAccessible?: boolean }
  > = {
    costMonitoring: {
      component: (
        <AgentCostSetup
          checked
          readonly
          hasBeenInstalled={alreadyInstalledFeatures?.costMonitoring}
        />
      ),
    },
    autoscaler: {
      component: (
        <Stack>
          <AgentAutoscalerSetup
            checked={featureSelection.autoscaler}
            hasBeenInstalled={alreadyInstalledFeatures?.autoscaler}
            onChange={(enabled) => updateFeature('autoscaler', enabled)}
          />
          {autoscalerSelectionProps.shouldShowAutoscalerSelection &&
            featureSelection.autoscaler && (
              <Box ml={32} mb={16}>
                <SelectAutoscalerType
                  autoscalerType={autoscalerSelectionProps.autoscalerType}
                  onChange={(autoscalerType) =>
                    autoscalerSelectionProps.onSelectAutoscalerType(
                      autoscalerType
                    )
                  }
                />
              </Box>
            )}
        </Stack>
      ),
    },
    workloadAutoscaler: {
      component: (
        <AgentWorkloadAutoscalerSetup
          checked={featureSelection.workloadAutoscaler}
          hasBeenInstalled={alreadyInstalledFeatures?.workloadAutoscaler}
          onChange={(enabled) => updateFeature('workloadAutoscaler', enabled)}
        />
      ),
      isAccessible: shouldShowFullFeatureSelection,
    },
    security: {
      component: isSecuritySunsetEnabled ? null : (
        <AgentSecuritySetup
          checked={featureSelection.security}
          hasBeenInstalled={alreadyInstalledFeatures?.security}
          onChange={(enabled) => updateFeature('security', enabled)}
        />
      ),
      isAccessible: !isSecuritySunsetEnabled && shouldShowFullFeatureSelection,
    },
    omni: {
      component: (
        <AgentOmniSetup
          readonly={!featureSelection.autoscaler}
          readonlyTooltip={
            !featureSelection.autoscaler
              ? `Select ${FEATURE_TO_TITLE.autoscaler} to enable this component`
              : undefined
          }
          checked={featureSelection.omni}
          hasBeenInstalled={alreadyInstalledFeatures?.omni}
          onChange={(enabled) => updateFeature('omni', enabled)}
        />
      ),
      isAccessible: shouldShowFullFeatureSelection,
    },
    networkCostMetrics: {
      component: (
        <AgentNetworkCostSetup
          checked={featureSelection.networkCostMetrics}
          hasBeenInstalled={alreadyInstalledFeatures?.networkCostMetrics}
          onChange={(enabled) => updateFeature('networkCostMetrics', enabled)}
        />
      ),
      isAccessible: shouldShowFullFeatureSelection,
    },
    // Reliability metrics is enabled via castctl only (see Castctl FeaturesSection).
    reliabilityMetrics: {
      component: null,
      isAccessible: false,
    },
    gpuMetricsExporter: {
      component: (
        <AgentGpuMetricsSetup
          checked={featureSelection.gpuMetricsExporter}
          hasBeenInstalled={alreadyInstalledFeatures?.gpuMetricsExporter}
          onChange={(enabled) => updateFeature('gpuMetricsExporter', enabled)}
        />
      ),
      isAccessible: shouldShowFullFeatureSelection,
    },
    nvidiaGpuPlugin: {
      component: (
        <AgentNvidiaDevicePluginSetup
          readonly={!featureSelection.autoscaler}
          readonlyTooltip={
            !featureSelection.autoscaler
              ? `Select ${FEATURE_TO_TITLE.autoscaler} to enable this component`
              : undefined
          }
          checked={featureSelection.nvidiaGpuPlugin}
          hasBeenInstalled={alreadyInstalledFeatures?.nvidiaGpuPlugin}
          onChange={(enabled) => updateFeature('nvidiaGpuPlugin', enabled)}
        />
      ),
      isAccessible: isEKSProvider && shouldShowFullFeatureSelection,
    },
    // kent is not part of this component
    kent: {
      component: null,
      isAccessible: false,
    },
  };

  const filterAccessible = (items: SetupComponentItem[]) =>
    items.filter((item) => item.isAccessible !== false);

  const mainFeatures = filterAccessible(
    MAIN_FEATURES.map((feature) => ({
      feature,
      ...featureComponentMap[feature],
    }))
  );

  const extraFeatures = filterAccessible(
    EXTRA_FEATURES.map((feature) => ({
      feature,
      ...featureComponentMap[feature],
    }))
  );

  return (
    <Stack data-testid="features-container">
      {mainFeatures.map(({ feature, component }) => (
        <Fragment key={feature}>{component}</Fragment>
      ))}

      {shouldShowFullFeatureSelection && (
        <Stack ml={6} mr={24}>
          <Typography
            variant="L10M"
            color={colorOrToken(
              'grey.400',
              tokens.theme.fg.structural.secondary
            )}
            mt={12}
            mb={12}
          >
            Extra components
          </Typography>
          {extraFeatures.map(({ feature, component }) => (
            <Fragment key={feature}>{component}</Fragment>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
